import React, { useState, useCallback } from 'react';
import VatFinancingCard from './VATFinancingCard';
import PropTypes from 'prop-types';
import { get, useChangeTrackingLens,set } from '@totalsoft/change-tracking-react';
import { useQueryWithErrorHandling } from 'customHooks/errorHandling';
import {VATFINANCING_DATA_QUERY} from './queries';
import { VATFINANCING_INPUT_MUTATION } from './mutations';
import { OFFER_VAT_FINANCING_UPDATED } from './subscriptions';
import { shape, fromModel, lessThan, logTo } from '@totalsoft/pure-validations';
import { isDirty } from '@totalsoft/change-tracking';
import { useValidation } from '@totalsoft/pure-validations-react';
import { useMutation } from '@apollo/client';
import { useError } from 'customHooks/errorHandling';
import { useTranslation } from 'react-i18next';
import { useGlobalSubscription } from 'providers/GlobalCacheManager';
import { useToast } from 'utils/hooks';
import { useServerErrorHandling } from 'utils/hooks/useServerErrorHandling';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { exportPaymentPlan } from 'features/offer/edit/financials/paymentPlan/actionCreators';

const validatorVATFinancing =
  fromModel(model =>
    shape({
      graceMonths: lessThan(model.durationMonths) 
    })
  ) |> logTo(console);

  const VatFinancingContainer = ({documentId, siteId}) => {

  const showError = useError();
  const addToast = useToast();
  const { t } = useTranslation();
  const [cardExpanded, setExpanded] = useState(false);
  const [vatFinancingLens, dirtyInfo, resetVatFinancingLens] = useChangeTrackingLens(null);
  const [validationVATFinancing, validateVATFinancing] = useValidation(validatorVATFinancing);
  const [saving, setSaving] = useState(false);
  const dispatch = useDispatch();
  
  const exportPaymentPlanWithTemplateOutput = useSelector(state => state.params.items.exportVatFinancingPaymentPlanWithTemplateId, shallowEqual);

  const onExpand = (_, expanded) => {
      setExpanded(!expanded);
    };

   const { storeKey } = useServerErrorHandling({
     effect: _ => {
      setSaving(false);
     }
   });

  const { loading } = useQueryWithErrorHandling(VATFINANCING_DATA_QUERY, {
    variables: { documentId, siteId },
    skip: !cardExpanded,
    onCompleted: data => {
      resetVatFinancingLens(data.vatFinancingDocument);
      }
    }
  );

  const exportPaymentPlanInternal = useCallback((fileType) => {    
    dispatch(exportPaymentPlan(documentId, siteId, t("OfferEdit.FinancialServices.ScheduleTitle"), fileType, true));
  }, [documentId, siteId]); // eslint-disable-line react-hooks/exhaustive-deps

  const [saveVatFinancing] = useMutation(VATFINANCING_INPUT_MUTATION, {
    skip: !cardExpanded,
    onError: error => {
      showError(error.message);
      setSaving(false);
    },
    onCompleted: r => {
      const { correlationId } = r.updateVatFinancing;
      storeKey({ correlationId });
    }
  });

  useGlobalSubscription(OFFER_VAT_FINANCING_UPDATED, {
    variables: {
      documentId,
      siteId
    },
    onSubscriptionData: ({
      subscriptionData: {
        data: { vatFinancingUpdated }
      }
    }) => {
      setSaving(false);
      resetVatFinancingLens(vatFinancingUpdated);
      addToast(t('OfferEdit.VATFinancing.VATFinancingUpdated'), 'success');
    }
  });

   const handlePropertyChange = prop => value => {
     let updatedData = {
       ...(vatFinancingLens |> get),
       [prop]: value};
     (vatFinancingLens |> set)(updatedData);
   };

   const handleSave = () => {
    let vatFincingData = vatFinancingLens |> get;
    if (!validateVATFinancing(vatFincingData)) return;
    saveVatFinancing({
      variables: {
        input: vatFincingData
      }
    });
    setSaving(true);
   }


   
   return (
     <VatFinancingCard 
         vatFinancingLens={vatFinancingLens}
         onPropertyChange={handlePropertyChange}
         cardExpanded={cardExpanded}
         onExpand={onExpand}
         loading={loading}
         onSave={handleSave}
         isDirty={isDirty(dirtyInfo)}
         saving={saving}
         validation={validationVATFinancing}
         exportPaymentPlan={exportPaymentPlanInternal}
         exportPaymentPlanWithTemplateOutput = {exportPaymentPlanWithTemplateOutput}
         />
           
    );
};

VatFinancingContainer.propTypes = {
  documentId: PropTypes.number.isRequired,
  siteId: PropTypes.number.isRequired
}

export default VatFinancingContainer;
